import React, { useContext, useState } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import styled from 'styled-components'
import { Button, DatePicker, Dropdown, Input, Modal, Spin, message } from 'antd'
import { DownOutlined, PlusOutlined } from '@ant-design/icons'
import CreateLegalNoticeModal from './CreateLegalNoticeModal'
import HtmlPreviewModal from './HtmlPreviewModal'
import MarkdownHistoryModal from './MarkdownHistoryModal'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import {
  LEGAL_NOTICES_QUERY,
  CREATE_LEGAL_NOTICE_MUTATION,
  UPDATE_DOCUMENT_MARKDOWN_MUTATION,
  UPDATE_LEGAL_NOTICE_MUTATION,
  LEGAL_DOCUMENT_MARKDOWN_HTML_QUERY,
  TRIGGER_LEGAL_NOTICE_EVENT_MUTATION,
} from 'graphql/LegalFormalities'
import { LegalNotice } from 'types/legalFormality'
import { LegalDocumentType } from 'types'
import { MarkdownVersion } from 'types/legalDocument'
import MilkdownEditor from 'components/MilkdownEditor'
import dayjs from 'dayjs'
import 'dayjs/locale/fr.js'
import frFR from 'antd/locale/fr_FR'

const getLatestMarkdown = (doc?: LegalDocumentType | null): string => {
  const versions = doc?.markdownContent as MarkdownVersion[] | null | undefined
  if (!versions || versions.length === 0) return ''
  return versions[versions.length - 1].content || ''
}

const Announces = (): JSX.Element => {
  const { legalFormality } = useContext(CurrentLegalFormalityContext)
  const [modalOpen, setModalOpen] = useState(false)
  const [provider, setProvider] = useState<string>('les_echos')
  const [providerReference, setProviderReference] = useState<string>('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [markdownValues, setMarkdownValues] = useState<Record<string, string>>(
    {}
  )
  const [savingId, setSavingId] = useState<string | null>(null)
  const [savingInfoId, setSavingInfoId] = useState<string | null>(null)
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null)
  const [triggeringEventId, setTriggeringEventId] = useState<string | null>(
    null
  )
  const [htmlModalOpen, setHtmlModalOpen] = useState(false)
  const [htmlNoticeId, setHtmlNoticeId] = useState<string | null>(null)
  const [historyNotice, setHistoryNotice] = useState<LegalNotice | null>(null)
  const [reapplyingVersion, setReapplyingVersion] = useState<number | null>(
    null
  )

  const { data, loading, refetch } = useQuery(LEGAL_NOTICES_QUERY, {
    variables: { id: legalFormality.id },
    fetchPolicy: 'network-only',
  })

  const historyVersions =
    (historyNotice?.legalDocument as LegalDocumentType | null)
      ?.markdownContent ?? []

  const [noticeInfoValues, setNoticeInfoValues] = useState<
    Record<string, { publicationDate?: string; departmentCode?: string }>
  >({})

  const [updateDocumentMarkdown] = useMutation(
    UPDATE_DOCUMENT_MARKDOWN_MUTATION
  )
  const [updateLegalNotice] = useMutation(UPDATE_LEGAL_NOTICE_MUTATION)
  const [triggerLegalNoticeEvent] = useMutation(
    TRIGGER_LEGAL_NOTICE_EVENT_MUTATION
  )
  const [createLegalNotice, { loading: creating }] = useMutation(
    CREATE_LEGAL_NOTICE_MUTATION
  )
  const [fetchMarkdownHtml, { data: htmlData, loading: htmlLoading }] =
    useLazyQuery(LEGAL_DOCUMENT_MARKDOWN_HTML_QUERY, {
      fetchPolicy: 'network-only',
    })

  const ACTIVE_STATUSES = ['editing', 'published', 'pending', 'validated']
  const notices: LegalNotice[] = [
    ...(data?.legalFormality?.legalNotices ?? []),
  ].sort((a, b) => {
    const activeA = ACTIVE_STATUSES.includes(a.status)
    const activeB = ACTIVE_STATUSES.includes(b.status)
    if (activeA !== activeB) return Number(activeB) - Number(activeA)
    return Number(b.id) - Number(a.id)
  })
  const canCreateNotice = !notices.some(n => ACTIVE_STATUSES.includes(n.status))

  const getMarkdown = (notice: LegalNotice): string => {
    if (notice.id in markdownValues) return markdownValues[notice.id]
    return getLatestMarkdown(notice.legalDocument as LegalDocumentType | null)
  }

  const handleMarkdownChange = (noticeId: string, value: string) => {
    setMarkdownValues(prev => ({ ...prev, [noticeId]: value }))
  }

  const getInfoValues = (notice: LegalNotice) => ({
    publicationDate:
      noticeInfoValues[notice.id]?.publicationDate !== undefined
        ? noticeInfoValues[notice.id].publicationDate
        : notice.publicationDate ?? '',
    departmentCode:
      noticeInfoValues[notice.id]?.departmentCode !== undefined
        ? noticeInfoValues[notice.id].departmentCode
        : notice.departmentCode ?? '',
  })

  const handleInfoChange = (
    noticeId: string,
    field: 'publicationDate' | 'departmentCode',
    value: string
  ) => {
    setNoticeInfoValues(prev => ({
      ...prev,
      [noticeId]: { ...prev[noticeId], [field]: value },
    }))
  }

  const handleSaveInfo = async (notice: LegalNotice) => {
    const { publicationDate, departmentCode } = getInfoValues(notice)
    setSavingInfoId(notice.id)
    try {
      const response = await updateLegalNotice({
        variables: {
          legalNoticeId: notice.id,
          publicationDate: publicationDate || null,
          departmentCode: departmentCode || null,
        },
      })
      const result = response.data?.legalNoticeUpdate
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success('Infos mises à jour')
        refetch()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur')
    } finally {
      setSavingInfoId(null)
    }
  }

  const handleSave = async (notice: LegalNotice) => {
    const doc = notice.legalDocument as LegalDocumentType | null
    if (!doc) return
    setSavingId(notice.id)
    try {
      const markdown = getMarkdown(notice)
      const response = await updateDocumentMarkdown({
        variables: {
          legalDocumentId: doc.id,
          content: btoa(unescape(encodeURIComponent(markdown))),
        },
      })
      const result = response.data?.legalFormalityUpdateDocumentMarkdown
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success('Document sauvegardé et PDF régénéré')
        refetch()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSavingId(null)
    }
  }

  const handleRegenerate = async (notice: LegalNotice) => {
    const doc = notice.legalDocument as LegalDocumentType | null
    if (!doc) return
    setRegeneratingId(notice.id)
    try {
      const response = await updateDocumentMarkdown({
        variables: {
          legalDocumentId: doc.id,
          regenerate: true,
        },
      })
      const result = response.data?.legalFormalityUpdateDocumentMarkdown
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success('Nouvelle version générée depuis le template')
        refetch()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la régénération')
    } finally {
      setRegeneratingId(null)
    }
  }

  const handleViewHtml = (notice: LegalNotice) => {
    const doc = notice.legalDocument as LegalDocumentType | null
    if (!doc) return
    setHtmlNoticeId(notice.id)
    setHtmlModalOpen(true)
    fetchMarkdownHtml({ variables: { legalDocumentId: doc.id } })
  }

  const handleCreate = async () => {
    try {
      const response = await createLegalNotice({
        variables: {
          legalFormalityId: parseInt(legalFormality.id),
          provider,
          providerReference: providerReference || null,
        },
      })
      const result = response.data?.legalNoticeCreate
      if (result?.errors?.length) {
        message.error(result.errors[0])
      } else {
        setModalOpen(false)
        setProviderReference('')
        refetch()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la création')
    }
  }

  const handleTriggerEvent = async (notice: LegalNotice, event: string) => {
    setTriggeringEventId(notice.id)
    try {
      const response = await triggerLegalNoticeEvent({
        variables: { legalNoticeId: notice.id, event },
      })
      const result = response.data?.legalNoticeTriggerEvent
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success(
          event === 'submit' ? 'Annonce soumise' : 'Annonce annulée'
        )
        refetch()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur')
    } finally {
      setTriggeringEventId(null)
    }
  }

  const handleReapply = async (version: MarkdownVersion) => {
    const doc = historyNotice?.legalDocument as LegalDocumentType | null
    if (!doc) return
    setReapplyingVersion(version.version)
    try {
      const response = await updateDocumentMarkdown({
        variables: {
          legalDocumentId: doc.id,
          content: btoa(unescape(encodeURIComponent(version.content))),
        },
      })
      const result = response.data?.legalFormalityUpdateDocumentMarkdown
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success(`Version v${version.version} réappliquée`)
        refetch()
        setHistoryNotice(null)
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la réapplication')
    } finally {
      setReapplyingVersion(null)
    }
  }

  if (loading)
    return (
      <Center>
        <Spin />
      </Center>
    )

  return (
    <Wrapper>
      <Header>
        <Title>Annonces légales</Title>
        <Button
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
          type="primary"
          disabled={!canCreateNotice}
        >
          Créer une annonce
        </Button>
      </Header>

      {notices.length === 0 ? (
        <Empty>
          <EmptyText>Pas de legal notice associée à cette formalité.</EmptyText>
          <Button
            onClick={() => setModalOpen(true)}
            icon={<PlusOutlined />}
            disabled={!canCreateNotice}
          >
            Créer une legal notice
          </Button>
        </Empty>
      ) : (
        <AccordionList>
          {notices.map(notice => {
            const isOpen = openId === notice.id
            return (
              <AccordionItem key={notice.id}>
                <AccordionHeader
                  onClick={() => setOpenId(isOpen ? null : notice.id)}
                >
                  <HeaderLeft>
                    <Badge>{notice.provider}</Badge>
                    <StatusBadge status={notice.status}>
                      {notice.status}
                    </StatusBadge>
                    {notice.externalRef && (
                      <span>réf: {notice.externalRef}</span>
                    )}
                    {notice.publicationDate && (
                      <span>
                        publication:{' '}
                        {dayjs(notice.publicationDate).format('DD/MM/YYYY')}
                      </span>
                    )}
                  </HeaderLeft>
                  <Meta>
                    #{notice.id} — créé le{' '}
                    {dayjs(notice.createdAt).format('DD/MM/YYYY HH:mm')}
                    <Chevron open={isOpen}>▾</Chevron>
                  </Meta>
                </AccordionHeader>

                {isOpen && (
                  <AccordionBody>
                    <Section>
                      <SectionTitle>Infos annonce légale</SectionTitle>
                      <Fields>
                        <Field>
                          <label>ID</label>
                          <span>{notice.id}</span>
                        </Field>
                        <Field>
                          <label>Provider</label>
                          <span>{notice.provider}</span>
                        </Field>
                        {notice.externalId && (
                          <Field>
                            <label>External ID</label>
                            <span>{notice.externalId}</span>
                          </Field>
                        )}
                        {notice.externalRef && (
                          <Field>
                            <label>Référence externe</label>
                            <span>{notice.externalRef}</span>
                          </Field>
                        )}
                        <Field>
                          <label>Date de publication</label>
                          <DatePicker
                            locale={frFR.DatePicker}
                            format="DD/MM/YYYY"
                            value={
                              getInfoValues(notice).publicationDate
                                ? dayjs(getInfoValues(notice).publicationDate)
                                : null
                            }
                            onChange={d =>
                              handleInfoChange(
                                notice.id,
                                'publicationDate',
                                d ? d.format('YYYY-MM-DD') : ''
                              )
                            }
                            size="small"
                            style={{ width: 140 }}
                          />
                        </Field>
                        <Field>
                          <label>Département</label>
                          <Input
                            value={getInfoValues(notice).departmentCode}
                            onChange={e =>
                              handleInfoChange(
                                notice.id,
                                'departmentCode',
                                e.target.value
                              )
                            }
                            size="small"
                            style={{ width: 80 }}
                            maxLength={3}
                            placeholder="ex: 75"
                          />
                        </Field>
                        <Field>
                          <label>Créé le</label>
                          <span>
                            {dayjs(notice.createdAt).format('DD/MM/YYYY HH:mm')}
                          </span>
                        </Field>
                      </Fields>
                      <div style={{ display: 'flex' }}>
                        <Button
                          size="small"
                          loading={savingInfoId === notice.id}
                          disabled={notice.status !== 'editing'}
                          onClick={() => handleSaveInfo(notice)}
                        >
                          Sauvegarder
                        </Button>
                      </div>
                    </Section>

                    {notice.legalDocument &&
                      getLatestMarkdown(
                        notice.legalDocument as LegalDocumentType
                      ) && (
                        <Section>
                          <MilkdownEditor
                            key={`${notice.legalDocument.id}-${
                              (notice.legalDocument as LegalDocumentType)
                                .markdownContent?.length ?? 0
                            }`}
                            defaultValue={getLatestMarkdown(
                              notice.legalDocument as LegalDocumentType
                            )}
                            onChange={val =>
                              handleMarkdownChange(notice.id, val)
                            }
                          />
                        </Section>
                      )}

                    {notice.legalDocument &&
                      !getLatestMarkdown(
                        notice.legalDocument as LegalDocumentType
                      ) && (
                        <div
                          style={{
                            padding: 20,
                            textAlign: 'center',
                            color: '#999',
                          }}
                        >
                          Aucun contenu markdown disponible pour ce document.
                        </div>
                      )}

                    <ActionRow>
                      <Dropdown
                        menu={{
                          items: [
                            ...(notice.legalDocument &&
                            getLatestMarkdown(
                              notice.legalDocument as LegalDocumentType
                            )
                              ? [
                                  {
                                    key: 'save',
                                    label: 'Sauvegarder',
                                    disabled:
                                      notice.status !== 'editing' ||
                                      savingId === notice.id,
                                  },
                                  {
                                    key: 'regenerate',
                                    label: 'Régénérer',
                                    disabled:
                                      notice.status !== 'editing' ||
                                      regeneratingId === notice.id,
                                  },
                                  {
                                    key: 'html',
                                    label: 'Voir HTML',
                                    disabled:
                                      htmlLoading && htmlNoticeId === notice.id,
                                  },
                                  { key: 'history', label: 'Historique' },
                                ]
                              : []),
                            ...(![
                              'published',
                              'canceled',
                              'validated',
                              'refused',
                            ].includes(notice.status)
                              ? [
                                  ...(notice.legalDocument &&
                                  getLatestMarkdown(
                                    notice.legalDocument as LegalDocumentType
                                  )
                                    ? [{ type: 'divider' as const }]
                                    : []),
                                  {
                                    key: 'cancel',
                                    label: 'Annuler',
                                    danger: true,
                                    disabled: triggeringEventId === notice.id,
                                  },
                                ]
                              : []),
                          ],
                          onClick: ({ key }) => {
                            if (key === 'save') handleSave(notice)
                            else if (key === 'regenerate')
                              handleRegenerate(notice)
                            else if (key === 'html') handleViewHtml(notice)
                            else if (key === 'history') setHistoryNotice(notice)
                            else if (key === 'cancel')
                              Modal.confirm({
                                title: "Annuler l'annonce légale",
                                content:
                                  'Cette action est irréversible. Confirmer ?',
                                okText: 'Oui, annuler',
                                okButtonProps: { danger: true },
                                cancelText: 'Non',
                                onOk: () =>
                                  handleTriggerEvent(notice, 'cancel'),
                              })
                          },
                        }}
                      >
                        <Button>
                          Actions <DownOutlined />
                        </Button>
                      </Dropdown>
                      {notice.status === 'editing' && (
                        <Button
                          type="primary"
                          loading={triggeringEventId === notice.id}
                          onClick={() => handleTriggerEvent(notice, 'submit')}
                        >
                          Soumettre
                        </Button>
                      )}
                    </ActionRow>
                  </AccordionBody>
                )}
              </AccordionItem>
            )
          })}
        </AccordionList>
      )}

      <CreateLegalNoticeModal
        open={modalOpen}
        confirmLoading={creating}
        provider={provider}
        onProviderChange={setProvider}
        providerReference={providerReference}
        onProviderReferenceChange={setProviderReference}
        onOk={handleCreate}
        onCancel={() => setModalOpen(false)}
      />
      <HtmlPreviewModal
        open={htmlModalOpen}
        loading={htmlLoading}
        html={htmlData?.legalDocumentMarkdownHtml?.html}
        onCancel={() => setHtmlModalOpen(false)}
      />
      <MarkdownHistoryModal
        open={historyNotice !== null}
        versions={historyVersions}
        reapplyingVersion={reapplyingVersion}
        canReapply={historyNotice?.status === 'editing'}
        onReapply={handleReapply}
        onClose={() => setHistoryNotice(null)}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
`

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
  border: 1px dashed #d0d3d8;
  border-radius: 8px;
`

const EmptyText = styled.p`
  color: #888;
  margin: 0;
`

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AccordionItem = styled.div`
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  overflow: hidden;
`

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  background: #f7f8f9;
  font-size: 13px;

  &:hover {
    background: #eceef1;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Badge = styled.span`
  background: #171a1c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 6px;
  text-transform: uppercase;
`

const StatusBadge = styled.span<{ status: string }>`
  background: ${p =>
    ['published', 'validated'].includes(p.status)
      ? '#22c55e'
      : ['error', 'canceled', 'refused'].includes(p.status)
      ? '#ef4444'
      : '#f59e0b'};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 6px;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #888;
  font-size: 12px;
`

const Chevron = styled.span<{ open: boolean }>`
  transition: transform 0.2s;
  transform: rotate(${p => (p.open ? '0deg' : '-90deg')});
  font-size: 16px;
`

const AccordionBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #fff;

  .milkdown-latex-inline-edit {
    display: none;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e6e8eb;
  padding-bottom: 6px;
`

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const Field = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;

  label {
    font-weight: 600;
    color: #555;
    min-width: 160px;
    flex-shrink: 0;
  }

  span {
    color: #222;
  }
`

export default Announces
